/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useEffect, useRef, useState } from "react";

function getValueFromLocalStorage<TValue = unknown>(key: string, defaultValue: TValue): TValue {
	const val = localStorage.getItem(key);

	if (val !== undefined && val !== null) {
		try {
			return JSON.parse(val);
		} catch (err) {
			console.warn("Failed to deserialized state from local storage", key, val, err);
			return defaultValue;
		}
	} else {
		return defaultValue;
	}
}

export type TUseLocalStorageUpdateFn<TValue = unknown> = (prevState: TValue) => TValue;

/**
 * Stores a state in a local storage
 *
 * @param key Key
 * @param defaultValue Default value to return if key does not exists
 */
export function useLocalStorage<TValue = unknown>(
	key: string,
	defaultValue: TValue
): [TValue, (value: TValue | TUseLocalStorageUpdateFn<TValue>) => void] {
	const [ state, updateState ] = useState(() => getValueFromLocalStorage(key, defaultValue));
	const beenInitialized = useRef(false);

	const setState = useCallback(
		(value: TValue | TUseLocalStorageUpdateFn<TValue>) => {
			if (typeof value === "function") {
				updateState((prevState) => {
					const newState = (value as TUseLocalStorageUpdateFn<TValue>)(prevState);
					localStorage.setItem(key, JSON.stringify(newState));
					return newState;
				});
			} else {
				localStorage.setItem(key, JSON.stringify(value));
				updateState(value);
			}
		},
		[ key ]
	);

	useEffect(() => {
		if (!beenInitialized.current) {
			beenInitialized.current = true;
			return;
		}

		updateState(getValueFromLocalStorage(key, defaultValue));
	}, [ key, defaultValue, beenInitialized ]);

	return [ state, setState ];
}
